import { useEffect, useState } from 'react';
import { api, type CompanySection, type Personnel, type CodeLibRow, type RateRow, type ImportReport, type WageRate, type LaborCalibration, type PendingUser, type UserRow } from '../api/client';
import { ROLES, ROLE_LABELS } from '@aisolution/shared';
import { useAuth } from '../auth/AuthContext';
import { useLang } from '../i18n/lang';
import { G } from '../i18n/glossary';

// Admin (§5.20). Company Profile content (proposals pull from here — Rule 2),
// personnel dropdown options, and the codes & standards library. Edits are
// enforced server-side (admin.settings); non-admins get a 403.
export function Admin() {
  const { t } = useLang();
  const [sections, setSections] = useState<CompanySection[]>([]);
  const [personnel, setPersonnel] = useState<Personnel[]>([]);
  const [codes, setCodes] = useState<CodeLibRow[]>([]);
  const [error, setError] = useState('');

  async function load() {
    try {
      const [cp, pn, cd] = await Promise.all([api.getCompanyProfile(), api.getPersonnel(), api.getCodes()]);
      setSections(cp.sections); setPersonnel(pn.personnel); setCodes(cd.codes);
    } catch (e) { setError(e instanceof Error ? e.message : t({ en: 'Failed to load', fil: 'Hindi ma-load' })); }
  }
  useEffect(() => { void load(); }, []);

  return (
    <div>
      <h1>{t({ en: 'Admin Settings', fil: 'Mga Setting ng Admin' })}</h1>
      <p className="muted">{t({ en: 'Company Profile, personnel and the codes library. Only admins can edit.', fil: 'Company Profile, personnel at ang codes library. Admin lang ang puwedeng mag-edit.' })}</p>
      {error && <div className="error">{error}</div>}

      <div className="card" style={{ marginBottom: 16 }}>
        <h2>Company Profile <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>{t({ en: '— proposals assemble from these blocks; the software invents nothing', fil: '— ang mga proposal ay binubuo mula sa mga block na ito; walang iniimbento ang software' })}</span></h2>
        {sections.map((s) => <ProfileEditor key={s.section} section={s} onSaved={load} setError={setError} />)}
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h2>Employee Registration <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>{t({ en: '— let estimators & salespeople create their own accounts', fil: '— hayaang gumawa ng sariling account ang mga estimator at salesperson' })}</span></h2>
        <PendingApprovals setError={setError} />
        <RegistrationToggle setError={setError} />
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h2>{t({ en: 'Users & access', fil: 'Users at access' })} <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>{t({ en: '— change a role or remove access when someone leaves (takes effect immediately)', fil: '— baguhin ang role o alisin ang access kapag may umalis (agad na magkakabisa)' })}</span></h2>
        <UsersEditor setError={setError} />
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h2>Personnel</h2>
        <PersonnelEditor personnel={personnel} onChange={load} setError={setError} />
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h2>Codes & Standards library <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>({codes.length})</span></h2>
        <CodesEditor codes={codes} onChange={load} setError={setError} />
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h2>{t({ en: 'Default markups & margins', fil: 'Default markups at margins' })} <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>{t({ en: "— seed every new bid AND set the floor estimators can't go below", fil: '— magtatakda sa bawat bagong bid AT mag-set ng floor na hindi puwedeng babaan ng mga estimator' })}</span></h2>
        <DefaultMarkupsEditor setError={setError} />
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h2>Labor & Wage Rates <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>{t({ en: '— prevailing wage & Davis-Bacon by county; used by the cost engine', fil: '— prevailing wage at Davis-Bacon kada county; ginagamit ng cost engine' })}</span></h2>
        <WageRatesEditor setError={setError} />
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h2>CA prevailing-wage auto-update <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>{t({ en: '— pulls current rates from the official DIR determinations', fil: '— kinukuha ang kasalukuyang rates mula sa opisyal na DIR determinations' })}</span></h2>
        <PrevailingWageUpdater setError={setError} />
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h2>Labor Calibration <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>{t({ en: '— learn real productivity from completed jobs; tunes labor hours on every bid', fil: '— natututo ng totoong productivity mula sa mga tapos na trabaho; ina-adjust ang labor hours sa bawat bid' })}</span></h2>
        <LaborCalibrationEditor setError={setError} />
      </div>

      <div className="card">
        <h2>Material Pricing — Rate Library <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>{t({ en: '— historical part pricing used by BOM repricing', fil: '— historical part pricing na ginagamit ng BOM repricing' })}</span></h2>
        <RatesEditor setError={setError} />
      </div>
    </div>
  );
}

function PrevailingWageUpdater({ setError }: { setError: (s: string) => void }) {
  const { t } = useLang();
  const [log, setLog] = useState<import('../api/client').WageUpdateRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState<import('../api/client').WageUpdateSummary | null>(null);
  function loadLog() { api.getWageUpdateLog().then((r) => setLog(r.log)).catch(() => {}); }
  useEffect(() => { loadLog(); }, []);
  async function run() {
    setBusy(true); setSummary(null);
    try { const s = await api.updatePrevailingWages(); setSummary(s); loadLog(); }
    catch (e) { setError(e instanceof Error ? e.message : t({ en: 'Update failed', fil: 'Nabigo ang update' })); }
    finally { setBusy(false); }
  }
  const color = (s: string) => s === 'applied' ? 'var(--teal)' : s === 'held' ? 'var(--warning)' : s === 'error' ? 'var(--danger)' : 'var(--gray-500, #888)';
  return (
    <>
      <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
        {t({
          en: 'Fetches the current CA DIR General Prevailing Wage Determinations for each county in the library and updates the low-voltage classifications (Sound & Comm Installer, LV Technician, Inside Wireman).',
          fil: 'Kinukuha ang kasalukuyang CA DIR General Prevailing Wage Determinations para sa bawat county sa library at ina-update ang low-voltage classifications (Sound & Comm Installer, LV Technician, Inside Wireman).',
        })}{' '}<strong>{t({
          en: 'Rates that pass the sanity checks are applied automatically; anything that looks like a parse error (wild value, >35% jump) is HELD for you to review',
          fil: 'Ang rates na pumasa sa sanity checks ay automatic na inilalapat; anumang mukhang parse error (kakaibang value, >35% jump) ay HELD para suriin mo',
        })}</strong>{t({ en: ' — it is never pushed into a bid. Davis-Bacon is project-specific (by WD number) and isn’t auto-pulled.', fil: ' — hindi ito kailanman ipinapasok sa isang bid. Ang Davis-Bacon ay project-specific (kada WD number) at hindi auto-pulled.' })}
      </p>
      <button className="btn btn-primary btn-sm" disabled={busy} onClick={run}>{busy ? t({ en: 'Fetching from DIR…', fil: 'Kinukuha mula sa DIR…' }) : t({ en: '↻ Update CA prevailing wages now', fil: '↻ I-update ang CA prevailing wages ngayon' })}</button>
      {summary && (
        <div className="banner" style={{ marginTop: 10, fontSize: 13 }}>
          {t({ en: 'DIR period', fil: 'DIR period' })} <strong>{summary.period}</strong>{summary.period_changed ? t({ en: ' (auto-advanced to the new determination ✓)', fil: ' (auto na inilipat sa bagong determination ✓)' }) : ''} · {summary.counties} {t({ en: 'counties', fil: 'counties' })} · <strong style={{ color: 'var(--teal)' }}>{summary.applied} {t({ en: 'applied', fil: 'inilapat' })}</strong> · <strong style={{ color: 'var(--warning)' }}>{summary.held} {t({ en: 'held for review', fil: 'held para suriin' })}</strong> · {summary.unchanged} {t({ en: 'unchanged', fil: 'hindi nagbago' })}{summary.errors ? ` · ${summary.errors} ${t({ en: 'errors', fil: 'errors' })}` : ''}
        </div>
      )}
      {log.length > 0 && (
        <table style={{ marginTop: 10 }}>
          <thead><tr><th>{t({ en: 'When', fil: 'Kailan' })}</th><th>County</th><th>Classification</th><th style={{ textAlign: 'right' }}>Base $/hr</th><th style={{ textAlign: 'right' }}>Fringe</th><th>{t(G.status)}</th></tr></thead>
          <tbody>
            {log.map((r, i) => (
              <tr key={i}>
                <td className="muted" style={{ fontSize: 12 }}>{r.run_at?.slice(0, 10)}</td>
                <td>{r.county}</td>
                <td>{r.classification}</td>
                <td style={{ textAlign: 'right' }}>{r.old_base != null && r.old_base !== r.new_base ? <span className="muted" style={{ fontSize: 11 }}>${r.old_base}→</span> : null}${r.new_base}</td>
                <td style={{ textAlign: 'right' }}>${r.new_fringe ?? '—'}</td>
                <td style={{ color: color(r.status), fontWeight: 600 }} title={r.reason ?? ''}>{r.status}{r.status === 'held' ? ' ⚠' : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function UsersEditor({ setError }: { setError: (s: string) => void }) {
  const { t } = useLang();
  const { user } = useAuth();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [busy, setBusy] = useState('');
  function load() { api.listUsers().then((r) => setUsers(r.users)).catch((e) => setError(e instanceof Error ? e.message : t({ en: 'Failed to load users', fil: 'Hindi ma-load ang users' }))); }
  useEffect(() => { load(); }, []);
  async function change(u: UserRow, patch: { role?: string; active?: boolean }) {
    setBusy(u.id);
    try { await api.updateUser(u.id, patch); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : t({ en: 'Update failed', fil: 'Nabigo ang update' })); }
    finally { setBusy(''); }
  }
  async function resetPw(u: UserRow) {
    if (!window.confirm(t({ en: `Reset ${u.name}'s password? They'll be required to set a new one at next sign-in.`, fil: `I-reset ang password ni ${u.name}? Kakailanganin nilang mag-set ng bago sa susunod na sign-in.` }))) return;
    setBusy(u.id);
    try { const r = await api.resetUserPassword(u.id); window.alert(t({ en: `Temporary password for ${u.name}:\n\n${r.temp_password}\n\nGive this to them. They must change it the next time they sign in.`, fil: `Temporary password para kay ${u.name}:\n\n${r.temp_password}\n\nIbigay ito sa kanila. Dapat nilang palitan ito sa susunod nilang pag-sign in.` })); }
    catch (e) { setError(e instanceof Error ? e.message : t({ en: 'Reset failed', fil: 'Nabigo ang reset' })); }
    finally { setBusy(''); }
  }
  if (!users.length) return <p className="muted">{t({ en: 'No users yet.', fil: 'Wala pang users.' })}</p>;
  return (
    <table style={{ marginTop: 4 }}>
      <thead><tr><th>{t(G.name)}</th><th>{t(G.email)}</th><th>{t(G.role)}</th><th>{t(G.status)}</th><th></th></tr></thead>
      <tbody>
        {users.map((u) => {
          const self = u.id === user?.id;
          return (
            <tr key={u.id} style={u.active ? undefined : { opacity: 0.55 }}>
              <td>{u.name}{self && <span className="muted"> {t({ en: '(you)', fil: '(ikaw)' })}</span>}{!u.approved && <span style={{ color: 'var(--warning)', fontSize: 11 }}> {t({ en: '· pending', fil: '· nakabinbin' })}</span>}</td>
              <td className="muted" style={{ fontSize: 12 }}>{u.email}</td>
              <td>
                <select value={u.role} disabled={self || busy === u.id} onChange={(e) => change(u, { role: e.target.value })}>
                  {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                </select>
              </td>
              <td>{u.active ? <span style={{ color: 'var(--teal)', fontWeight: 600 }}>{t({ en: 'Active', fil: 'Aktibo' })}</span> : <span className="muted">{t({ en: 'Removed', fil: 'Inalis' })}</span>}</td>
              <td style={{ textAlign: 'right' }}>
                {self ? <span className="muted" style={{ fontSize: 11 }}>—</span> : (
                  <span style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
                    <button className="btn btn-ghost btn-sm" disabled={busy === u.id} onClick={() => resetPw(u)}>{t({ en: 'Reset password', fil: 'I-reset ang password' })}</button>
                    {u.active
                      ? <button className="btn btn-ghost btn-sm" disabled={busy === u.id} onClick={() => { if (window.confirm(t({ en: `Remove access for ${u.name}? They will be signed out immediately and can't sign back in.`, fil: `Alisin ang access ni ${u.name}? Agad silang masa-sign out at hindi na maka-sign in.` }))) change(u, { active: false }); }}>{t({ en: 'Remove access', fil: 'Alisin ang access' })}</button>
                      : <button className="btn btn-ghost btn-sm" disabled={busy === u.id} onClick={() => change(u, { active: true })}>{t({ en: 'Restore', fil: 'Ibalik' })}</button>}
                  </span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function DefaultMarkupsEditor({ setError }: { setError: (s: string) => void }) {
  const { t } = useLang();
  const [rows, setRows] = useState<{ name: string; label: string; pct: number; basis: string }[]>([]);
  const [edit, setEdit] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState('');
  function load() { api.getDefaultMarkups().then((r) => setRows(r.markups)).catch((e) => setError(e instanceof Error ? e.message : t({ en: 'Failed to load', fil: 'Hindi ma-load' }))); }
  useEffect(() => { load(); }, []);
  const val = (m: { name: string; pct: number }) => edit[m.name] ?? String(m.pct);
  async function save() {
    try {
      await api.saveDefaultMarkups(rows.map((m) => ({ name: m.name, pct: Number(edit[m.name] ?? m.pct) })));
      setEdit({}); setMsg(t({ en: 'Saved — new bids start here and estimators can’t bid below these.', fil: 'Na-save — dito magsisimula ang mga bagong bid at hindi puwedeng bumaba ang mga estimator sa mga ito.' })); setTimeout(() => setMsg(''), 3000); load();
    } catch (e) { setError(e instanceof Error ? e.message : t({ en: 'Save failed', fil: 'Nabigo ang pag-save' })); }
  }
  return (
    <>
      <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
        {t({
          en: 'Calibrated from 3DTSI’s won bids (~30% gross margin). Every new bid starts from these, and they are the floor — estimators can raise a markup but not go below it (sales tax excepted, since it varies by county). Changing a default only affects NEW bids.',
          fil: 'Naka-calibrate mula sa mga napanalunang bid ng 3DTSI (~30% gross margin). Dito nagsisimula ang bawat bagong bid, at ito ang floor — puwedeng itaas ng estimator ang markup pero hindi puwedeng babaan ito (maliban sa sales tax, dahil iba-iba kada county). Ang pagbabago ng default ay sa BAGONG bid lang nakakaapekto.',
        })}
      </p>
      {msg && <div className="banner">{msg}</div>}
      <table style={{ marginTop: 4 }}>
        <thead><tr><th>{t({ en: 'Markup / margin', fil: 'Markup / margin' })}</th><th style={{ width: 90 }}>%</th><th>{t({ en: 'Basis', fil: 'Basis' })}</th></tr></thead>
        <tbody>
          {rows.map((m) => (
            <tr key={m.name}>
              <td>{m.label}</td>
              <td><input style={{ width: 80 }} value={val(m)} onChange={(e) => setEdit((s) => ({ ...s, [m.name]: e.target.value }))} /></td>
              <td className="muted">{m.basis}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <button className="btn btn-primary btn-sm" style={{ marginTop: 10 }} onClick={save}>{t({ en: 'Save defaults', fil: 'I-save ang defaults' })}</button>
    </>
  );
}

function PendingApprovals({ setError }: { setError: (s: string) => void }) {
  const { t } = useLang();
  const [pending, setPending] = useState<PendingUser[] | null>(null);
  const [busy, setBusy] = useState('');
  async function load() {
    try { const r = await api.getPendingUsers(); setPending(r.pending); }
    catch (e) { setError(e instanceof Error ? e.message : t({ en: 'Failed to load pending users', fil: 'Hindi ma-load ang pending users' })); }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  async function approve(u: PendingUser) {
    setBusy(u.id); try { await api.approvePendingUser(u.id); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : t({ en: 'Approve failed', fil: 'Nabigo ang pag-approve' })); } finally { setBusy(''); }
  }
  async function reject(u: PendingUser) {
    setBusy(u.id); try { await api.rejectPendingUser(u.id); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : t({ en: 'Reject failed', fil: 'Nabigo ang pag-reject' })); } finally { setBusy(''); }
  }
  if (pending === null) return null;
  if (!pending.length) return null;   // nothing waiting → no clutter
  return (
    <div className="banner banner-danger" style={{ marginBottom: 12 }}>
      <strong>{pending.length} {t({ en: pending.length === 1 ? 'account awaiting approval:' : 'accounts awaiting approval:', fil: 'account na naghihintay ng approval:' })}</strong>
      <table style={{ marginTop: 6 }}>
        <thead><tr><th>{t(G.name)}</th><th>{t(G.email)}</th><th>{t(G.role)}</th><th>{t({ en: 'Registered', fil: 'Nag-register' })}</th><th></th></tr></thead>
        <tbody>
          {pending.map((u) => (
            <tr key={u.id}>
              <td>{u.name}</td><td>{u.email}</td><td>{u.role}</td><td className="muted" style={{ fontSize: 12 }}>{(u.created_at || '').slice(0, 10)}</td>
              <td style={{ whiteSpace: 'nowrap' }}>
                <button className="btn btn-primary btn-sm" disabled={busy === u.id} onClick={() => approve(u)}>{t({ en: 'Approve', fil: 'Aprubahan' })}</button>
                <button className="btn btn-ghost btn-sm" disabled={busy === u.id} onClick={() => reject(u)}>{t({ en: 'Reject', fil: 'Tanggihan' })}</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RegistrationToggle({ setError }: { setError: (s: string) => void }) {
  const { t } = useLang();
  const [open, setOpen] = useState<boolean | null>(null);
  const [domain, setDomain] = useState('@3dtsi.com');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.getRegistration().then((r) => { setOpen(r.open); setDomain(r.domain); }).catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'));
  }, [setError]);
  async function toggle(next: boolean) {
    setBusy(true);
    try { const r = await api.setRegistration(next); setOpen(r.open); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed to save'); }
    finally { setBusy(false); }
  }
  if (open === null) return <p className="muted">{t(G.loading)}</p>;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button className={`btn btn-sm ${open ? 'btn-primary' : 'btn-ghost'}`} disabled={busy} onClick={() => toggle(true)}>{t({ en: 'Registration ON', fil: 'Registration ON' })}</button>
        <button className={`btn btn-sm ${!open ? 'btn-primary' : 'btn-ghost'}`} disabled={busy} onClick={() => toggle(false)}>{t({ en: 'Registration OFF', fil: 'Registration OFF' })}</button>
        <span className="muted" style={{ fontSize: 13 }}>
          {t({ en: 'Currently', fil: 'Kasalukuyang' })} <strong style={{ color: open ? 'var(--teal-dark)' : 'var(--danger)' }}>{open ? t({ en: 'OPEN', fil: 'BUKAS' }) : t({ en: 'CLOSED', fil: 'SARADO' })}</strong>.
        </span>
      </div>
      <p className="muted" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
        {t({ en: 'When ON, employees can self-register from the sign-in screen — but only with a', fil: 'Kapag ON, puwedeng mag-self-register ang mga empleyado mula sa sign-in screen — pero gamit lang ang' })} <strong>{domain}</strong> {t({
          en: 'email, and only as an Estimator or Salesperson (never admin). New sign-ups are',
          fil: 'email, at bilang Estimator o Salesperson lang (hindi admin). Ang mga bagong sign-up ay',
        })} <strong>{t({ en: 'pending', fil: 'pending' })}</strong> {t({
          en: 'until you approve them above, so no one can sign in just by knowing the email domain. Their bids go in their own folder; every signed-in user can open and work on any bid. Turn OFF to stop new sign-ups.',
          fil: 'hanggang aprubahan mo sila sa itaas, kaya walang maka-sign in basta alam lang ang email domain. Napupunta sa sariling folder ang kanilang mga bid; bawat naka-sign in na user ay puwedeng magbukas at magtrabaho sa kahit anong bid. I-OFF para itigil ang mga bagong sign-up.',
        })}
      </p>
    </div>
  );
}

const CAL_LABEL: Record<string, string> = {
  electrician: 'Electrician (Inside Wireman)', sound_comm: 'Sound & Communication Installer', technician: 'Low-Voltage Technician',
};
function LaborCalibrationEditor({ setError }: { setError: (s: string) => void }) {
  const { t } = useLang();
  const [rows, setRows] = useState<LaborCalibration[]>([]);
  const [add, setAdd] = useState<Record<string, { est: string; act: string }>>({});
  async function load() {
    try { const r = await api.getLaborCalibration(); setRows(r.calibration); }
    catch (e) { setError(e instanceof Error ? e.message : t({ en: 'Failed to load calibration', fil: 'Hindi ma-load ang calibration' })); }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  async function addJob(cat: string) {
    const a = add[cat]; if (!a?.est || !a?.act) return;
    try { await api.updateLaborCalibration(cat, { add_est: Number(a.est), add_actual: Number(a.act) }); setAdd((s) => ({ ...s, [cat]: { est: '', act: '' } })); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : t({ en: 'Save failed', fil: 'Nabigo ang pag-save' })); }
  }
  async function reset(cat: string) { try { await api.updateLaborCalibration(cat, { reset: true }); await load(); } catch { /* ignore */ } }

  return (
    <div>
      <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
        {t({ en: 'After a job closes, enter the', fil: 'Pagkatapos magsara ng trabaho, ilagay ang' })} <strong>{t({ en: 'estimated', fil: 'estimated' })}</strong> {t({ en: 'vs.', fil: 'kumpara sa' })} <strong>{t({ en: 'actual', fil: 'actual' })}</strong> {t({
          en: 'field hours for each trade. The factor (Σ actual ÷ Σ estimated) then tunes labor hours on every future bid. Empty = 1.0 (no change) until you add real data.',
          fil: 'field hours para sa bawat trade. Ang factor (Σ actual ÷ Σ estimated) ay ina-adjust ang labor hours sa bawat susunod na bid. Walang laman = 1.0 (walang pagbabago) hanggang magdagdag ka ng totoong data.',
        })}
      </p>
      <table>
        <thead><tr><th>Classification</th><th>{t({ en: 'Jobs', fil: 'Trabaho' })}</th><th>Est. hrs</th><th>Actual hrs</th><th>{t({ en: 'Factor', fil: 'Factor' })}</th><th>{t({ en: 'Add a closed job (est / actual)', fil: 'Magdagdag ng saradong trabaho (est / actual)' })}</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.category}>
              <td>{CAL_LABEL[r.category] ?? r.category}</td>
              <td>{r.jobs}</td>
              <td>{r.est_hours.toLocaleString()}</td>
              <td>{r.actual_hours.toLocaleString()}</td>
              <td><strong style={{ color: r.factor > 1 ? 'var(--danger)' : r.factor < 1 ? 'var(--teal-dark)' : undefined }}>{r.factor.toFixed(3)}×</strong></td>
              <td style={{ whiteSpace: 'nowrap' }}>
                <input style={{ width: 70 }} placeholder="est" value={add[r.category]?.est ?? ''} onChange={(e) => setAdd((s) => ({ ...s, [r.category]: { est: e.target.value, act: s[r.category]?.act ?? '' } }))} />
                {' / '}
                <input style={{ width: 70 }} placeholder="actual" value={add[r.category]?.act ?? ''} onChange={(e) => setAdd((s) => ({ ...s, [r.category]: { est: s[r.category]?.est ?? '', act: e.target.value } }))} />
                <button className="btn btn-ghost btn-sm" onClick={() => addJob(r.category)}>{t({ en: '+ Add', fil: '+ Idagdag' })}</button>
                {r.jobs > 0 && <button className="btn btn-ghost btn-sm" onClick={() => reset(r.category)} title={t({ en: 'Clear accumulated data', fil: 'Burahin ang naipong data' })}>{t(G.reset)}</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const WAGE_TYPES = [
  { v: 'open', label: 'Open shop' }, { v: 'ca_prevailing', label: 'CA prevailing (DIR)' },
  { v: 'nv_prevailing', label: 'NV prevailing' }, { v: 'davis_bacon', label: 'Davis-Bacon (federal)' },
];
const CLASSIFICATIONS = ['Electrician (Inside Wireman)', 'Sound & Communication Installer', 'Low-Voltage Technician'];

function WageRatesEditor({ setError }: { setError: (s: string) => void }) {
  const { t } = useLang();
  const [rates, setRates] = useState<WageRate[]>([]);
  const [wageType, setWageType] = useState('ca_prevailing');
  const [edit, setEdit] = useState<Record<string, Record<string, string>>>({});
  const [add, setAdd] = useState({ classification: CLASSIFICATIONS[0], state: 'CA', county: '', base_rate: '', fringe: '', burden_pct: '0.22' });

  async function load() {
    try { const r = await api.getWageRates({ wage_type: wageType }); setRates(r.rates); }
    catch (e) { setError(e instanceof Error ? e.message : t({ en: 'Failed to load wage rates', fil: 'Hindi ma-load ang wage rates' })); }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [wageType]);

  const loaded = (base: number, fringe: number, burden: number) => Math.round((base * (1 + (burden || 0)) + fringe) * 100) / 100;
  const ev = (id: string, f: string, dflt: string | number) => edit[id]?.[f] ?? String(dflt ?? '');
  const setE = (id: string, f: string, v: string) => setEdit((s) => ({ ...s, [id]: { ...(s[id] ?? {}), [f]: v } }));

  async function saveRow(r: WageRate) {
    const e = edit[r.id] ?? {};
    try {
      await api.updateWageRate(r.id, {
        base_rate: e.base_rate !== undefined ? Number(e.base_rate) : undefined,
        fringe: e.fringe !== undefined ? Number(e.fringe) : undefined,
        burden_pct: e.burden_pct !== undefined ? Number(e.burden_pct) : undefined,
        county: e.county, is_sample: false,   // editing marks it as confirmed (no longer sample)
      });
      setEdit((s) => { const n = { ...s }; delete n[r.id]; return n; });
      await load();
    } catch (err) { setError(err instanceof Error ? err.message : t({ en: 'Save failed', fil: 'Nabigo ang pag-save' })); }
  }
  async function addRate() {
    if (!add.base_rate) return;
    try {
      await api.addWageRate({
        classification: add.classification, wage_type: wageType,
        state: add.state || null, county: add.county || null,
        base_rate: Number(add.base_rate), fringe: Number(add.fringe) || 0, burden_pct: Number(add.burden_pct) || 0,
        wage_source_ref: 'Manual entry', is_sample: false,
      });
      setAdd({ ...add, county: '', base_rate: '', fringe: '' });
      await load();
    } catch (err) { setError(err instanceof Error ? err.message : t({ en: 'Add failed', fil: 'Nabigo ang pagdagdag' })); }
  }

  return (
    <div>
      <div className="banner banner-danger" style={{ fontSize: 12, marginBottom: 10 }}>
        {t({ en: 'Pre-loaded prevailing-wage / Davis-Bacon rates are', fil: 'Ang naka-pre-load na prevailing-wage / Davis-Bacon rates ay' })} <strong>{t({ en: 'STARTER estimates', fil: 'STARTER estimates' })}</strong> {t({ en: 'flagged', fil: 'na may flag na' })}
        <span className="pill pill-needs_review" style={{ margin: '0 4px' }}>sample</span> {t({
          en: '— verify each against the current official CA DIR / DOL determination for the project county before bidding. Editing a rate marks it confirmed.',
          fil: '— i-verify ang bawat isa laban sa kasalukuyang opisyal na CA DIR / DOL determination para sa county ng proyekto bago mag-bid. Ang pag-edit ng rate ay nagmamarka nito bilang confirmed.',
        })}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
        <label style={{ fontSize: 13, fontWeight: 600 }}>{t({ en: 'Wage basis:', fil: 'Wage basis:' })}</label>
        <select value={wageType} onChange={(e) => setWageType(e.target.value)}>
          {WAGE_TYPES.map((w) => <option key={w.v} value={w.v}>{w.label}</option>)}
        </select>
        <span className="muted" style={{ fontSize: 12 }}>{rates.length} {t({ en: 'rate(s)', fil: 'rate(s)' })}</span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead><tr><th>Classification</th><th>{t({ en: 'State', fil: 'State' })}</th><th>County</th><th>Base $/hr</th><th>Fringe $/hr</th><th>{t({ en: 'Burden', fil: 'Burden' })}</th><th>Loaded $/hr</th><th></th></tr></thead>
          <tbody>
            {rates.map((r) => {
              const base = Number(ev(r.id, 'base_rate', r.base_rate));
              const fr = Number(ev(r.id, 'fringe', r.fringe));
              const bu = Number(ev(r.id, 'burden_pct', r.burden_pct));
              const dirty = !!edit[r.id];
              return (
                <tr key={r.id}>
                  <td style={{ fontSize: 12 }}>{r.classification}{r.is_sample ? <span className="pill pill-needs_review" style={{ marginLeft: 4 }}>sample</span> : ''}</td>
                  <td>{r.state ?? '—'}</td>
                  <td><input style={{ width: 90 }} value={ev(r.id, 'county', r.county ?? '')} onChange={(e) => setE(r.id, 'county', e.target.value)} /></td>
                  <td><input style={{ width: 70 }} value={ev(r.id, 'base_rate', r.base_rate)} onChange={(e) => setE(r.id, 'base_rate', e.target.value)} /></td>
                  <td><input style={{ width: 70 }} value={ev(r.id, 'fringe', r.fringe)} onChange={(e) => setE(r.id, 'fringe', e.target.value)} /></td>
                  <td><input style={{ width: 56 }} value={ev(r.id, 'burden_pct', r.burden_pct)} onChange={(e) => setE(r.id, 'burden_pct', e.target.value)} /></td>
                  <td><strong>${loaded(base, fr, bu).toFixed(2)}</strong></td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {dirty && <button className="btn btn-primary btn-sm" onClick={() => saveRow(r)}>{t(G.save)}</button>}
                    <button className="btn btn-ghost btn-sm" onClick={async () => { await api.deleteWageRate(r.id); await load(); }}>✕</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 10, borderTop: '1px solid var(--gray-100)', paddingTop: 10 }}>
        <select value={add.classification} onChange={(e) => setAdd({ ...add, classification: e.target.value })}>
          {CLASSIFICATIONS.map((cl) => <option key={cl} value={cl}>{cl}</option>)}
        </select>
        <input style={{ width: 50 }} value={add.state} onChange={(e) => setAdd({ ...add, state: e.target.value })} placeholder="ST" />
        <input style={{ width: 100 }} value={add.county} onChange={(e) => setAdd({ ...add, county: e.target.value })} placeholder="County" />
        <input style={{ width: 70 }} value={add.base_rate} onChange={(e) => setAdd({ ...add, base_rate: e.target.value })} placeholder="Base $" />
        <input style={{ width: 70 }} value={add.fringe} onChange={(e) => setAdd({ ...add, fringe: e.target.value })} placeholder="Fringe $" />
        <input style={{ width: 56 }} value={add.burden_pct} onChange={(e) => setAdd({ ...add, burden_pct: e.target.value })} placeholder="Burden" />
        <button className="btn btn-ghost btn-sm" onClick={addRate}>{t({ en: '+ Add rate', fil: '+ Magdagdag ng rate' })}</button>
      </div>
    </div>
  );
}

function RatesEditor({ setError }: { setError: (s: string) => void }) {
  const { t } = useLang();
  const [rates, setRates] = useState<RateRow[]>([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [report, setReport] = useState<ImportReport | null>(null);
  const [busy, setBusy] = useState('');

  async function load() {
    try {
      const r = await api.getRates({ limit: 50, q: q || undefined });
      setRates(r.rates); setTotal(r.total);
    } catch (e) { setError(e instanceof Error ? e.message : t({ en: 'Failed to load rates', fil: 'Hindi ma-load ang rates' })); }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(t({ en: `Importing ${f.name}…`, fil: `Ini-import ang ${f.name}…` })); setReport(null);
    try {
      const text = await f.text();
      const r = await api.importRatesCsv(text);
      setReport(r.report);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t({ en: 'Import failed', fil: 'Nabigo ang import' }));
    } finally {
      setBusy(''); e.target.value = '';
    }
  }

  async function wipe() {
    if (!confirm(t({ en: `Delete ALL ${total} pricing rows from the library? This cannot be undone (but you can re-import the CSV).`, fil: `Burahin ang LAHAT ng ${total} pricing rows sa library? Hindi na ito maibabalik (pero puwede mong i-import muli ang CSV).` }))) return;
    setBusy(t({ en: 'Wiping library…', fil: 'Binubura ang library…' }));
    try {
      const r = await api.wipeRates();
      setReport(null);
      alert(t({ en: `Removed ${r.wiped} rows.`, fil: `Inalis ang ${r.wiped} rows.` }));
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : t({ en: 'Wipe failed', fil: 'Nabigo ang pagbura' })); }
    finally { setBusy(''); }
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 10 }}>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>{t({ en: 'Bulk-import CSV (procore export or sp_static)', fil: 'Bulk-import CSV (procore export o sp_static)' })}</label>
          <input type="file" accept=".csv,text/csv" onChange={onFile} disabled={!!busy} />
        </div>
        <button className="btn btn-ghost btn-sm" onClick={wipe} disabled={!!busy || total === 0} title={t({ en: 'Delete every row (re-import to restore)', fil: 'Burahin ang bawat row (i-import muli para ibalik)' })}>
          {t({ en: 'Wipe library', fil: 'Burahin ang library' })}
        </button>
      </div>
      <small className="muted">
        {t({
          en: 'Auto-filters: rows with HR/LS units (labor / lump-sum), unit price < $0.10 (placeholders), or no part_number AND no manufacturer are skipped.',
          fil: 'Auto-filters: nila-laktawan ang rows na may HR/LS units (labor / lump-sum), unit price < $0.10 (placeholders), o walang part_number AT walang manufacturer.',
        })}{' '}
        {t({ en: 'Source mapping:', fil: 'Source mapping:' })} <code>procore_historical</code> → distributor tier, <code>sp_static</code> → historical tier.{' '}
        {t({ en: 'Imported rates auto-apply on the next', fil: 'Ang mga na-import na rate ay automatic na inilalapat sa susunod na' })} <strong>BOM &gt; Reprice</strong>.
      </small>

      {busy && <div className="muted" style={{ marginTop: 8 }}>{busy}</div>}

      {report && (
        <div className="banner" style={{ marginTop: 12 }}>
          <strong>{t({ en: `Imported ${report.inserted} of ${report.total_csv_rows} rows.`, fil: `Na-import ang ${report.inserted} sa ${report.total_csv_rows} rows.` })}</strong>{' '}
          {t({ en: 'By source:', fil: 'Ayon sa source:' })} {Object.entries(report.by_source).map(([s, n]) => `${s}=${n}`).join(', ')}.{' '}
          {t({ en: `Skipped: ${report.skipped_labor_or_lump} labor/LS, ${report.skipped_placeholder} placeholders, ${report.skipped_no_part} no part/mfr, ${report.skipped_duplicate} duplicates.`, fil: `Nilaktawan: ${report.skipped_labor_or_lump} labor/LS, ${report.skipped_placeholder} placeholders, ${report.skipped_no_part} walang part/mfr, ${report.skipped_duplicate} duplicates.` })}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', margin: '12px 0' }}>
        <div className="field" style={{ marginBottom: 0, flex: 1 }}>
          <label>{t({ en: 'Search (part / mfr / description / item)', fil: 'Maghanap (part / mfr / description / item)' })}</label>
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void load(); }} placeholder="e.g. Avigilon, HID, Cat6" />
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => void load()}>{t(G.search)}</button>
      </div>

      <p className="muted" style={{ fontSize: 13 }}>{t({ en: `${rates.length} of ${total} shown`, fil: `${rates.length} sa ${total} ang ipinapakita` })}</p>
      <table>
        <thead><tr><th>Mfr</th><th>Part</th><th>Item</th><th>Unit $</th><th>Unit</th><th>{t({ en: 'Supplier', fil: 'Supplier' })}</th><th>{t({ en: 'Quote date', fil: 'Quote date' })}</th><th>{t({ en: 'Source', fil: 'Source' })}</th></tr></thead>
        <tbody>
          {rates.map((r) => (
            <tr key={r.id}>
              <td>{r.manufacturer ?? '—'}</td>
              <td><strong>{r.part_number ?? '—'}</strong></td>
              <td title={r.description ?? ''}>{(r.item_name ?? r.description ?? '').slice(0, 60)}</td>
              <td style={{ textAlign: 'right' }}>${r.unit_price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
              <td>{r.unit ?? '—'}</td>
              <td>{(r.supplier ?? '').slice(0, 40)}</td>
              <td>{r.quote_date ?? '—'}{r.stale ? ' ⚠' : ''}</td>
              <td><span className="pill pill-proposed">{r.source}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProfileEditor({ section, onSaved, setError }: { section: CompanySection; onSaved: () => void; setError: (s: string) => void }) {
  const { t } = useLang();
  const [body, setBody] = useState(section.body);
  const [saving, setSaving] = useState(false);
  const dirty = body !== section.body;
  async function save() {
    setSaving(true);
    try { await api.updateCompanyProfile(section.section, { body }); onSaved(); }
    catch (e) { setError(e instanceof Error ? e.message : t({ en: 'Save failed (admin only)', fil: 'Nabigo ang pag-save (admin lang)' })); }
    finally { setSaving(false); }
  }
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <strong>{section.title}</strong>
        {section.is_placeholder ? <span className="pill pill-needs_review">{t({ en: 'placeholder', fil: 'placeholder' })}</span> : <span className="pill pill-confirmed">{t({ en: 'edited', fil: 'na-edit' })}</span>}
      </div>
      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3}
        style={{ width: '100%', padding: 8, border: '1px solid var(--gray-300)', borderRadius: 6, font: 'inherit', marginTop: 4 }} />
      {dirty && <button className="btn btn-ghost btn-sm" onClick={save} disabled={saving}>{saving ? t(G.saving) : t(G.save)}</button>}
    </div>
  );
}

function PersonnelEditor({ personnel, onChange, setError }: { personnel: Personnel[]; onChange: () => void; setError: (s: string) => void }) {
  const { t } = useLang();
  const [name, setName] = useState('');
  const [role, setRole] = useState('estimator');
  async function add() {
    if (!name.trim()) return;
    try { await api.addPersonnel({ name: name.trim(), role }); setName(''); onChange(); }
    catch (e) { setError(e instanceof Error ? e.message : t({ en: 'Add failed (admin only)', fil: 'Nabigo ang pagdagdag (admin lang)' })); }
  }
  return (
    <>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t(G.name)} />
        <select value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="estimator">Estimator</option><option value="salesperson">Salesperson</option>
          <option value="project_manager">Project Manager</option><option value="engineer">Engineer</option>
        </select>
        <button className="btn btn-ghost btn-sm" onClick={add}>{t(G.add)}</button>
      </div>
      <table style={{ marginTop: 10 }}>
        <tbody>
          {personnel.map((p) => (
            <tr key={p.id}><td><strong>{p.name}</strong></td><td className="muted">{p.role}</td>
              <td><button className="btn btn-ghost btn-sm" onClick={async () => { try { await api.deletePersonnel(p.id); onChange(); } catch (e) { setError(`${e}`); } }}>✕</button></td></tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function CodesEditor({ codes, onChange, setError }: { codes: CodeLibRow[]; onChange: () => void; setError: (s: string) => void }) {
  const { t } = useLang();
  const [code, setCode] = useState('');
  const [title, setTitle] = useState('');
  const [level, setLevel] = useState('federal');
  async function add() {
    if (!code.trim()) return;
    try { await api.addCode({ level, code: code.trim(), title }); setCode(''); setTitle(''); onChange(); }
    catch (e) { setError(e instanceof Error ? e.message : t({ en: 'Add failed (admin only)', fil: 'Nabigo ang pagdagdag (admin lang)' })); }
  }
  return (
    <>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={level} onChange={(e) => setLevel(e.target.value)}>
          <option value="federal">Federal</option><option value="state">State</option>
          <option value="county">County</option><option value="trade">Trade</option>
        </select>
        <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code (e.g. NFPA 72)" />
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t({ en: 'Title', fil: 'Pamagat' })} style={{ flex: 1, minWidth: 160 }} />
        <button className="btn btn-ghost btn-sm" onClick={add}>{t(G.add)}</button>
      </div>
      <table style={{ marginTop: 10 }}>
        <thead><tr><th>{t({ en: 'Level', fil: 'Antas' })}</th><th>Code</th><th>{t({ en: 'Title', fil: 'Pamagat' })}</th><th>{t({ en: 'Applies to', fil: 'Saklaw' })}</th></tr></thead>
        <tbody>
          {codes.map((c) => (
            <tr key={c.id}><td className="muted">{c.level}</td><td><strong>{c.code}</strong></td><td>{c.title}</td><td className="muted">{c.applies_to_system}</td></tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
